import { useCallback, useMemo } from 'react';

import { dateWithTime } from 'utils/date';

import { useLocale } from '../useSitecoreContext';

/**
 * Format a date according to the current locale, e.g. '2012-12-20' for sv-SE
 * or '2/20/2012' for en-US.
 */
export function useDateFormatter() {
	const { locale } = useLocale();

	const formatter = useMemo(
		() => new Intl.DateTimeFormat(locale, { dateStyle: 'short' }),
		[locale],
	);

	return useCallback(
		(date: string | Date | undefined): string =>
			// Force noon time to handle time zone shifts. A real world example is
			// a user that had a tab open without reloading and a time zone shift
			// occurred (either through travel or DST change). The browser then
			// somehow takes both the previous and new time zone into account when
			// parsing and can shift a date that has a time close to midnight.
			// This resulted in an offer displaying as valid to one day longer
			// than it should.
			// Setting to noon should handle everything except extremes like
			// traveling from New Zealand to Europe.
			date ? formatter.format(dateWithTime(new Date(date), 12)) : '',
		[formatter],
	);
}

/**
 * Format a date with time according to the current locale, e.g.
 * '2012-12-20 04:00' for sv-SE or '12/20/12, 4:00 AM' for en-US.
 */
export function useDateTimeFormatter() {
	const { locale } = useLocale();

	const formatter = useMemo(
		() =>
			new Intl.DateTimeFormat(locale, {
				dateStyle: 'short',
				timeStyle: 'short',
			}),
		[locale],
	);

	return useCallback(
		(date: string | Date | undefined): string =>
			date ? formatter.format(new Date(date)) : '',
		[formatter],
	);
}
